import { DonutChartEntry, InternalEntry, InternalOverflowEntry } from './types';

import { colorWheel } from '~/theme/colors';

export function processEntries(
  entries: DonutChartEntry[],
  isSorted: boolean | undefined,
  hasOverflow: boolean | undefined
) {
  const internalEntries = getInternalEntries(entries, isSorted);
  const total = entries.reduce((acc, d) => acc + d.value, 0);
  const formatPercent = (value: number) => getPercentString((value * 100) / total);

  if (!hasOverflow)
    return {
      entries: internalEntries,
      formatPercent,
    };

  return {
    entries: getInternalEntriesWithOverflowEntry(internalEntries),
    formatPercent,
  };
}

export function getInternalEntries(
  entries: DonutChartEntry[],
  isSorted?: boolean
): InternalEntry[] {
  return entries
    .map((d, i) => ({
      ...d,
      color: d.color ?? colorWheel[i % colorWheel.length],
    }))
    .filter((d) => d.value >= 0)
    .sort((a, b) => (isSorted ? b.value - a.value : 0));
}

export function getInternalEntriesWithOverflowEntry(
  entries: InternalEntry[]
): (InternalEntry | InternalOverflowEntry)[] {
  const total = entries.reduce((acc, d) => acc + d.value, 0);
  // The minimum value for determining overflow default to 5% of the total.
  // However, as a general rule, we do not want the overflow entry to be greater
  // than 50% of the total. If the default minimum value causes this to happen,
  // we will instead use the largest minimum percentage that does not result in
  // overflow taking a majority of the donut chart .
  const defaultMinValue = 0.05 * total;
  let highestMinValue = 0;
  let currentValue = 0;
  let currentTotal = total;
  entries
    .sort((a, b) => b.value - a.value)
    .forEach(({ value }) => {
      if (currentTotal > 0.5 * total) {
        currentTotal -= value;
        currentValue = value;
        return;
      }
      if (highestMinValue) return;
      // NOTE: Because we use <= for filtering, the highest minimum value is
      // the value *after* whichever value crosses the 50% line.
      if (value !== currentValue) highestMinValue = value;
    });

  const minValue = Math.min(defaultMinValue, highestMinValue);
  // Split entries into displayed and overflow based on the minimum value
  const displayedEntries = entries.filter(({ value }) => value > minValue);
  const overflowEntries = entries
    .filter(({ value }) => value <= minValue)
    .sort((a, b) => b.value - a.value);

  // If there is not more than one entry for overflow, we will just show all
  // entries as provided, since it will not make visual difference.
  if (overflowEntries.length <= 1) return entries;

  // Otherwise, group all the overflow entries into a single 'Other' entry and
  // append after the displayed entries.
  const overflowEntry: InternalOverflowEntry = {
    label: 'Other',
    value: overflowEntries.reduce((acc, d) => acc + d.value, 0),
    entries: overflowEntries.map((entry) => ({ ...entry, color: 'transparent' })),
    color: 'var(--colors-background-2)',
  };
  return [...displayedEntries, overflowEntry];
}

export function getPercentString(percent: number) {
  if (percent < 1) return '<1%';
  if (percent.toFixed(1).endsWith('.0')) return `${percent.toFixed(0)}%`;
  return `${percent.toFixed(1)}%`;
}
